import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AlertService, AuthService } from '@core/services';
import { APP_ROUTES, AUTH_KEYS, AuthErrorAlert, getAuthErrorAlert } from '@core/constants';

@Injectable()
/**
 * @description Prevents access to routes when users is already logged in
 * @redirectsTo products-dashboard component
 * */
export class AnonymousGuard {
  constructor(
    private auth: AuthService,
    private router: Router,
    private alertService: AlertService
  ) {}

  canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Promise<boolean> {
    return this.returnAuthorizedOrRedirect(state?.url);
  }

  canActivateChild(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Promise<boolean> {
    return this.returnAuthorizedOrRedirect(state?.url);
  }

  private async returnAuthorizedOrRedirect(targetUrl?: string): Promise<boolean> {
    if (this.auth.isAuthenticationProcessing) {
      if (this.auth.isAuthenticated && this.auth.hasPendingRequiredActions) {
        this.navigateToRequiredActions(this.getSafeReturnUrl(targetUrl));
        return false;
      }

      if (this.auth.isAuthenticated && this.returnAuthErrorToDashboard(targetUrl)) {
        return false;
      }

      return true;
    }

    if (!this.auth.isAuthenticated && !this.isOAuthCallbackRoute(targetUrl)) {
      await this.auth.loadExistingAuthSession();
    }

    if (this.auth.isAuthenticated) {
      const returnUrl = this.getSafeReturnUrl(targetUrl);

      if (this.auth.hasPendingRequiredActions) {
        this.navigateToRequiredActions(returnUrl);
        return false;
      }

      if (this.returnAuthErrorToDashboard(targetUrl)) {
        return false;
      }

      if (returnUrl) {
        this.router.navigateByUrl(returnUrl);
      } else {
        this.router.navigate([APP_ROUTES.DASHBOARD]);
      }
      return false;
    }

    return true;
  }

  private navigateToRequiredActions(returnUrl: string | null): void {
    if (returnUrl) {
      this.router.navigate([APP_ROUTES.REQUIRED_ACTIONS], {
        queryParams: { [AUTH_KEYS.RETURN_URL]: returnUrl }
      });
      return;
    }

    this.router.navigate([APP_ROUTES.REQUIRED_ACTIONS]);
  }

  private getSafeReturnUrl(targetUrl?: string): string | null {
    const query = targetUrl?.split('?')[1];
    const returnUrl = query ? new URLSearchParams(query).get(AUTH_KEYS.RETURN_URL) : null;

    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return null;
    }

    const path = returnUrl.split('?')[0].replace(/^\/+/, '');
    return path === APP_ROUTES.AUTH_CALLBACK || path === APP_ROUTES.REQUIRED_ACTIONS
      ? null
      : returnUrl;
  }

  private isOAuthCallbackRoute(targetUrl?: string): boolean {
    const path = targetUrl?.split('?')[0].replace(/^\/+/, '');

    return path === APP_ROUTES.AUTH_CALLBACK;
  }

  private returnAuthErrorToDashboard(targetUrl?: string): boolean {
    const authError = targetUrl
      ? this.router.parseUrl(targetUrl).queryParamMap.get(AUTH_KEYS.AUTH_ERROR)
      : null;
    const alert: AuthErrorAlert | undefined = authError ? getAuthErrorAlert(authError) : undefined;

    if (!alert) {
      return false;
    }

    this.alertService.setAlertOnNavigate(APP_ROUTES.DASHBOARD, alert);
    return true;
  }
}
